import { http } from '@/lib/http';
import type { HireFormField, HireFormSchema } from './types';
import {
  FORM_KEY_SEPARATOR,
  toWorkdayOption,
  type WorkdayReference,
} from '../workdayReferences';

/**
 * Hire-form dropdowns are loaded differently from Kombo's.
 *
 * Kombo's referenced fields embed a first page of entries and are searched via
 * `kombo/referenced_options?link=…`. The hire form embeds nothing and instead
 * hands back a ready-made URL, which is followed verbatim. Pointing the Kombo
 * loader at it would nest one endpoint inside another and return nothing, so
 * these fields get their own loader.
 */

/**
 * Walks the nested schema and flattens whatever `pick` returns for each leaf,
 * keyed by the name the converter will give that field.
 */
const collectFromSchema = (
  schema: HireFormSchema | undefined,
  pick: (_field: HireFormField) => string | undefined,
  prefix = ''
): Record<string, string> => {
  if (!schema) return {};

  return Object.entries(schema).reduce<Record<string, string>>(
    (collected, [key, field]) => {
      if (!field || typeof field !== 'object') return collected;
      const name = prefix ? `${prefix}${FORM_KEY_SEPARATOR}${key}` : key;

      if (field.type === 'object' && field.properties) {
        return {
          ...collected,
          ...collectFromSchema(field.properties, pick, name),
        };
      }

      const picked = pick(field as HireFormField);
      if (picked) collected[name] = picked;
      return collected;
    },
    {}
  );
};

/**
 * Prefilled values, keyed by the name the converter will give the field, so the
 * label for each can be resolved before the form renders.
 */
export const collectPrefilledValues = (
  schema: HireFormSchema | undefined
): Record<string, string> =>
  collectFromSchema(schema, (field) =>
    typeof field.value === 'string' ? field.value : undefined
  );

/**
 * Every referenced select's link, keyed by the name the converter will give the
 * field — so the loaders can be attached to the built config afterwards.
 */
export const collectReferenceLinks = (
  schema: HireFormSchema | undefined
): Record<string, string> =>
  collectFromSchema(schema, (field) =>
    typeof field.options?.link === 'string' ? field.options.link : undefined
  );

/**
 * Fetches one list, following the link exactly as given.
 *
 * `name_searchable` is always false — Workday has no server-side search — so
 * the whole list comes back once and the client filters it. The largest is job
 * profiles at ~1,750.
 *
 * Deliberately does no deduping of its own. Currency_ID is referenced by three
 * fields and Compensation_Plan_ID by two, and they all mount at once — but the
 * caller passes a React Query-backed `fetchList`, which already collapses
 * concurrent requests for one key. A second dedupe layer here would cache the
 * same list twice under two different keys.
 */
export const fetchReferenceOptions = async (
  link: string
): Promise<{ id: string; label: string }[]> => {
  // The link already carries `/api/v1`, which `@/lib/http` also prefixes.
  // The endpoint also accepts `&refresh=true` to bypass its 15-minute cache;
  // nothing asks for that, matching the position form.
  const response = await http.get(link.replace(/^\/api\/v1/, ''));
  const results: WorkdayReference[] = response?.data?.results || [];
  return results.map(toWorkdayOption);
};

/**
 * Attaches a loader to every referenced select in a built FormBuilder config.
 *
 * Done after conversion rather than before because the converter is shared with
 * the Kombo employee form, and the two load their options from different
 * places.
 */
export const applyHireOptionLoaders = async (
  config: Record<string, unknown>[],
  linksByName: Record<string, string>,
  /**
   * Optional cache-aware fetcher. The hook passes one backed by React Query so
   * a list survives closing and reopening the form.
   */
  fetchList: (
    _link: string
  ) => Promise<{ id: string; label: string }[]> = fetchReferenceOptions,
  /** Prefilled values, so their labels can be resolved up front. */
  valuesByName: Record<string, string> = {}
): Promise<Record<string, unknown>[]> =>
  Promise.all(
    config.map(async (item) => {
      if (!item || typeof item !== 'object') return item;

      const fields = item.fields as Record<string, unknown>[] | undefined;
      if (Array.isArray(fields)) {
        return {
          ...item,
          fields: await applyHireOptionLoaders(
            fields,
            linksByName,
            fetchList,
            valuesByName
          ),
        };
      }

      const name = item.name as string;
      const link = linksByName[name];
      if (!link) return item;

      let cached: { id: string; label: string }[] | undefined;
      const loadOptions = async (inputValue?: string) => {
        if (!cached) cached = await fetchList(link);
        const search = (inputValue || '').trim().toLowerCase();
        return search
          ? cached.filter((option) =>
              option.label.toLowerCase().includes(search)
            )
          : cached;
      };

      // A prefilled value can only be displayed if its option is in `options`:
      // AsyncSelectField resolves the label from that list alone, never from
      // what `customLoadOptions` returns. Resolve it now so the field shows a
      // name rather than a raw id or an empty "Search…".
      const prefilled = valuesByName[name];
      let options: { id: string; label: string }[] = [];
      if (prefilled) {
        try {
          const list = await fetchList(link);
          options = [
            list.find((option) => option.id === prefilled) ?? {
              id: prefilled,
              label: prefilled,
            },
          ];
        } catch {
          options = [{ id: prefilled, label: prefilled }];
        }
      }

      return {
        ...item,
        type: 'asyncSelect',
        valueKey: 'id',
        labelKey: 'label',
        loadDefaultOptions: true,
        isClearable: true,
        customLoadOptions: loadOptions,
        options,
      };
    })
  );
